import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useUser, useAuth } from '@clerk/clerk-react';
import {
  logHomepageEvent,
  trackNewsletterSignupAttempt,
  logEvent,
} from "../analytics";
import { PageShell } from "./layout";
import PartnerLandingContent from "./PartnerLandingContent";
import { useGlobalModal } from "../contexts/ModalContext";
import styles from "../styles/HomePage.module.css";
import globalStyles from "../styles/global.module.css";
import SnackAlert from "./SnackAlert";
import UserSettings from "./UserSettings";
import WelcomeMessage from "./WelcomeMessage";
import Testimonials from "./Testimonials";
import Button from "./Button";
import { toDateFormat, getStatusText, getActionButtonText, getCurrentDate, hasNewsletterSignupCompleted, buildCurrentWeekDataFromDates, getCurrentWeekCompletion, GAME_STATUS } from "../util";
import StreakIcons from "./StreakIcons";
import { submitNewsletterSignup } from "../api/newsletter";
import { syncUserProfileDebounced } from "../api/users";
import { setCategoryConsent } from "../utils/consentPreferences";
import { resolveNewsletterSignupAlert } from "../utils/newsletterGettoncini";
import { queueResourceAlertsFromPayload } from "../utils/resourceAlerts";
import { getPlayersCount } from "../api/stats";
import { getPartnerCampaign, resolvePartnerImageUrl } from "../api/partners";
import { useGettoncini } from "../contexts/GettonciniContext";
import { useStellineRewards } from "../contexts/StellineRewardsContext";
import { useAlertQueueActions } from "../contexts/AlertQueueContext";
import { getAllGames, getGameMessage, getGameConfig } from "../games";
import { useStreak } from '../contexts/StreakContext';
import { useGameStatuses } from '../contexts/GameStatusesContext';
import { GettonciniIcon } from '../icons';
import GameCatalogSection from './GameCatalog';
import FaIcon from './FaIcon';
import StellineRewardChip from './StellineRewardChip';
import { darkenColor, isLightColor } from '../utils/colors';

// Utility: check if a date string (YYYY-MM-DD) matches today's date in the user's browser
export function isToday(date) {
  const today = new Date();
  const todayStr = toDateFormat(today);
  return date === todayStr;
}

// Utility: format a date string (YYYY-MM-DD) as 'Domenica 15 giugno' in Italian
export function formatItalianDate(dateStr) {
  if (!dateStr || dateStr === 'Oggi' || dateStr === 'Ieri' || dateStr === 'Domani') return dateStr;
  const months = [
    'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
    'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
  ];
  const days = [
    'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
  ];
  const [yyyy, mm, dd] = dateStr.split('-');
  const dateObj = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const dayName = days[dateObj.getDay()];
  const monthName = months[dateObj.getMonth()];
  return `${dayName} ${Number(dd)} ${monthName}`;
}

// Utility: get Italian day of week from date string (YYYY-MM-DD)
export function getItalianDayOfWeek(dateStr) {
  if (!dateStr || dateStr === 'Oggi' || dateStr === 'Ieri' || dateStr === 'Domani') return dateStr;
  const days = [
    'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
  ];
  const [yyyy, mm, dd] = dateStr.split('-');
  const dateObj = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return days[dateObj.getDay()];
}

const buildDailyCircuitThemeStyle = (backgroundColor) => {
  const isLightBackground = isLightColor(backgroundColor);
  const cardTextColor = isLightBackground ? '#151515' : '#ffffff';
  const mutedTextColor = isLightBackground ? 'rgba(21, 21, 21, 0.72)' : 'rgba(255, 255, 255, 0.82)';
  const borderColor = isLightBackground
    ? darkenColor(backgroundColor, 18)
    : 'rgba(255, 255, 255, 0.28)';

  return {
    '--daily-circuit-card-bg': backgroundColor,
    '--daily-circuit-card-text': cardTextColor,
    '--daily-circuit-card-muted': mutedTextColor,
    '--daily-circuit-card-border': borderColor,
  };
};

// Utility: get Italian day of week with date and month (e.g., "Venerdì 27.06")
export function getItalianDayWithDate(dateStr) {
  if (!dateStr || dateStr === 'Oggi' || dateStr === 'Ieri' || dateStr === 'Domani') return dateStr;
  const days = [
    'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
  ];
  const [yyyy, mm, dd] = dateStr.split('-');
  const dateObj = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const dayName = days[dateObj.getDay()];
  const day = Number(dd);
  const month = Number(mm);
  return `${dayName} ${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}`;
}

// Utility: check if a date string (YYYY-MM-DD) is in the future (after today)
export function isFuture(date) {
  const today = new Date();
  const todayStr = toDateFormat(today);
  return date > todayStr;
}

// --- Carousel Component ---
export const Carousel = ({ items, renderItem }) => {
  const trackRef = useRef(null);

  // Set initial scroll position to center the 'today' card before paint
  React.useLayoutEffect(() => {
    const vw = window.innerWidth / 100;
    // Find the index of the 'today' card
    const todayIndex = items.findIndex(item => isToday(item.date));
    if (todayIndex === -1) return;

    // Card and layout dimensions
    const padding = 20; // px
    const gap = 18; // px
    const todayCardWidth = 80 * vw; // 80vw for today card
    const otherCardWidth = 35 * vw; // 35vw for other cards (adjust if your cards are 30vw)

    // Calculate start position of the today card
    const startOfTodayCard =
      padding + // left padding
      todayIndex * otherCardWidth + // widths of previous cards
      todayIndex * gap; // gaps before today card

    // Center of today card
    const centerOfTodayCard = startOfTodayCard + todayCardWidth / 2;

    // Scroll so that the center of today card is at the center of the viewport
    const scrollLeft = centerOfTodayCard - window.innerWidth / 2;

    if (trackRef.current) {
      trackRef.current.scrollLeft = scrollLeft;
    }
  }, [items]);

  return (
    <div className={styles.carousel}>
      <div
        className={styles.carouselTrack}
        ref={trackRef}
        tabIndex={0}
        style={{ scrollBehavior: 'auto' }}
      >
        {items.map((item, idx) => (
          <div
            key={idx}
            className={styles.carouselCard}
          >
            {renderItem(item, false)}
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Base Card Component ---
const BaseCard = ({
  title,
  gameId,
  pathName,
  date,
  icon,
  message,
  graphic,
  backgroundColor = '#fff',
  style = {},
  className = '',
  renderContent,
  locked = false,
  onLockedClick,
  status,
}) => {
  const cardRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0 });

  // Touch events
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    dragState.current = { dragging: false, startX: touch.clientX, startY: touch.clientY };
  };
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - dragState.current.startX);
    const dy = Math.abs(touch.clientY - dragState.current.startY);
    if (dx > 10 || dy > 10) dragState.current.dragging = true;
  };
  const handleTouchEnd = (e) => {
    // nothing needed here
  };
  const handleClick = (e) => {
    if (dragState.current.dragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Track card click analytics
    const gameSlug = gameId;
    const today = toDateFormat(new Date());
    const isToday = date === today;
    const isFuture = date > today;
    
    if (isFuture) {
      // Track future game click (locked)
      logHomepageEvent('future_game_clicked', {
        game_slug: gameSlug,
        target_date: date,
        is_locked: true
      });
    } else if (isToday) {
      // Track today's game click
      logHomepageEvent('today_game_clicked', {
        game_slug: gameSlug,
        game_status: status
      });
    } else {
      // Track past game click
      logHomepageEvent('past_game_clicked', {
        game_slug: gameSlug,
        game_status: status,
        days_ago: Math.floor((new Date(today) - new Date(date)) / (1000 * 60 * 60 * 24))
      });
    }
  };

  // Mouse events (for desktop drag)
  const handleMouseDown = (e) => {
    dragState.current = { dragging: false, startX: e.clientX, startY: e.clientY };
  };
  const handleMouseMove = (e) => {
    if (dragState.current.startX !== undefined) {
      const dx = Math.abs(e.clientX - dragState.current.startX);
      const dy = Math.abs(e.clientY - dragState.current.startY);
      if (dx > 10 || dy > 10) dragState.current.dragging = true;
    }
  };
  const handleMouseUp = (e) => {
    // nothing needed here
  };

  const to = `/${pathName}/${date}`;

  if (locked) {
    return (
      <div
        className={`${styles.gameCard} ${className} ${styles.locked}`}
        style={{ background: backgroundColor, ...style }}
        ref={cardRef}
        onClick={onLockedClick}
        tabIndex={0}
        role="button"
        aria-disabled="true"
      >
        {renderContent
          ? renderContent({ title, message, date, graphic, status })
          : <>
              <div className={styles.gameCardHeader}>
                <div className={styles.gameCardTitle}>{title}</div>
                <div className={styles.gameCardDate}>{formatItalianDate(date)}</div>
              </div>
              <div className={styles.gameCardGraphic}>{graphic}</div>
              {message && <div className={styles.gameCardMessage}>{message}</div>}
              {icon}
            </>
        }
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={`${styles.gameCard} ${className}`}
      style={{ background: backgroundColor, ...style }}
      ref={cardRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      tabIndex={0}
    >
      {renderContent
        ? renderContent({ title, message, date, graphic, status })
        : <>
            <div className={styles.gameCardHeader}>
              <div className={styles.gameCardTitle}>{title}</div>
              <div className={styles.gameCardDate}>{formatItalianDate(date)}</div>
            </div>
            <div className={styles.gameCardGraphic}>{graphic}</div>
            {message && <div className={styles.gameCardMessage}>{message}</div>}
            {icon}
          </>
      }
    </Link>
  );
};

// --- TodayCard: 75vw wide ---
export const TodayCard = ({
  title,
  gameId,
  pathName,
  date,
  icon,
  message,
  graphic,
  backgroundColor = '#fff',
  style = {},
  className = '',
  locked = false,
  onLockedClick,
  status,
  isNew,
}) => (
  <BaseCard
    title={title}
    gameId={gameId}
    pathName={pathName}
    date={date}
    icon={icon}
    message={message}
    graphic={graphic}
    backgroundColor={backgroundColor}
    style={{ ...style }}
    className={`${styles.todayCard} ${isNew ? styles.isNewCard : ''} ${className}`}
    renderContent={({ title, message, date }) => (
      <div className={styles.todayCardContentRow}>
        <div className={styles.todayCardLeft}>
          <div className={styles.todayCardTitle}>{title}</div>
          <div className={styles.todayCardMessage}>{message}</div>
          <div className={styles.todayCardDate}>{formatItalianDate(date)}</div>
        </div>
        <div className={styles.todayCardRight}>
          {icon}
        </div>
      </div>
    )}
    locked={locked}
    onLockedClick={onLockedClick}
    status={status}
  />
);

// --- OtherCard: 30vw wide ---
export const OtherCard = ({
  title,
  gameId,
  pathName,
  date,
  icon,
  backgroundColor = '#fff',
  style = {},
  className = '',
  locked = false,
  onLockedClick,
  status,
}) => (
  <BaseCard
    title={title}
    gameId={gameId}
    pathName={pathName}
    date={date}
    icon={icon}
    backgroundColor={backgroundColor}
    style={{ width: '30vw', maxWidth: 160, minWidth: 110, ...style }}
    className={`${styles.otherCard} ${className}`}
    renderContent={({ date, status }) => (
      <div className={styles.otherCardContent}>
        {icon}
        {status && (
          <div className={styles.statusIndicator}>
            {getStatusText(status)}
          </div>
        )}
        <div className={styles.otherCardDay}>{getItalianDayOfWeek(date)}</div>
      </div>
    )}
    locked={locked}
    onLockedClick={onLockedClick}
    status={status}
  />
);

// --- DesktopOtherCard: Desktop-specific version with date format ---
export const DesktopOtherCard = ({
  title,
  gameId,
  pathName,
  date,
  icon,
  backgroundColor = '#fff',
  style = {},
  className = '',
  locked = false,
  onLockedClick,
  status,
}) => (
  <BaseCard
    title={title}
    gameId={gameId}
    pathName={pathName}
    date={date}
    icon={icon}
    backgroundColor={backgroundColor}
    style={{ width: '30vw', maxWidth: 160, minWidth: 110, ...style }}
    className={`${styles.otherCard} ${className}`}
    renderContent={({ date, status }) => (
      <div className={styles.otherCardContent}>
        {icon}
        {status && (
          <div className={styles.statusIndicator}>
            {getStatusText(status)}
          </div>
        )}
        <div className={styles.otherCardDay}>{getItalianDayWithDate(date)}</div>
      </div>
    )}
    locked={locked}
    onLockedClick={onLockedClick}
    status={status}
  />
);

const getGreeting = (firstName = null) => {
  const hour = new Date().getHours();
  const namePart = firstName ? `, ${firstName}` : "";
  
  if (hour >= 5 && hour < 11) {
    return `Buongiorno${namePart}! Nuovi giochini ti aspettano.`;
  } else if (hour >= 11 && hour < 15) {
    return `Ciao${namePart}! Pronto/a per una sfida di parole?`;
  } else if (hour >= 15 && hour < 19) {
    return `Buon pomeriggio${namePart}! Allena la mente con i nostri giochini.`;
  } else if (hour >= 19 && hour < 23) {
    return `Buonasera${namePart}! Un po' di enigmi per rilassarsi?`;
  } else {
    return `Ciao, nottambulo! Le parole italiane non dormono mai.`;
  }
};

// --- Desktop Layout Component ---
const DesktopGameSection = ({ games, showLocked, onLockedClick }) => {
  const { isSignedIn } = useUser();
  const today = toDateFormat(new Date());
  
  // Find today's game
  const todayGame = games.find(game => isToday(game.date));
  // Find tomorrow's game
  const tomorrowGame = games.find(game => isFuture(game.date));
  // Get past games (excluding today and future)
  const pastGames = games
    .filter(game => !isToday(game.date) && !isFuture(game.date))
    .sort((a, b) => new Date(b.date) - new Date(a.date)) // Reverse chronological order
    .slice(0, 6); // Show last 6 days

  return (
    <div className={styles.desktopGameSection}>
      {/* Top row: Today and Tomorrow */}
      <div className={styles.desktopTopRow}>
        <div className={styles.desktopTodayContainer}>
          <TodayCard
            title={todayGame.title}
            gameId={todayGame.gameId}
            pathName={todayGame.pathName}
            date={todayGame.date}
            icon={todayGame.icon}
            message={todayGame.message}
            graphic={todayGame.graphic}
            backgroundColor={todayGame.backgroundColor}
            status={todayGame.status}
            isNew={todayGame.isNew}
          />
        </div>
        <div className={styles.desktopTomorrowContainer}>
          <DesktopOtherCard
            title={tomorrowGame.title}
            gameId={tomorrowGame.gameId}
            pathName={tomorrowGame.pathName}
            date={tomorrowGame.date}
            icon={tomorrowGame.icon}
            backgroundColor={tomorrowGame.backgroundColor}
            locked={showLocked && isFuture(tomorrowGame.date) && !isSignedIn}
            onLockedClick={() => onLockedClick(`/${tomorrowGame.pathName}/${tomorrowGame.date}`)}
            status={tomorrowGame.status}
          />
        </div>
      </div>
      
      {/* Bottom row: Past games */}
      <div className={styles.desktopPastRow}>
        {pastGames.map((game, idx) => (
          <div key={idx} className={styles.desktopPastCard}>
            <DesktopOtherCard
              title={game.title}
              gameId={game.gameId}
              pathName={game.pathName}
              date={game.date}
              icon={game.icon}
              backgroundColor={game.backgroundColor}
              status={game.status}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const FullCarousel = ({ items, showLocked, onLockedClick }) => {
  const { isSignedIn } = useUser();
  return (
    <Carousel
      items={items}
      renderItem={(item, _, idx) => {
        const locked = showLocked && isFuture(item.date) && !isSignedIn;
        return isToday(item.date)
          ? <TodayCard
              title={item.title}
              gameId={item.gameId}
              pathName={item.pathName}
              date={item.date}
              icon={item.icon}
              message={item.message}
              graphic={item.graphic}
              backgroundColor={item.backgroundColor}
              locked={locked}
              onLockedClick={() => onLockedClick(`/${item.pathName}/${item.date}`)}
              status={item.status}
              isNew={item.isNew}
            />
          : <OtherCard
              title={item.title}
              gameId={item.gameId}
              pathName={item.pathName}
              date={item.date}
              icon={item.icon}
              message={item.message}
              graphic={item.graphic}
              backgroundColor={item.backgroundColor}
              locked={locked}
              onLockedClick={() => onLockedClick(`/${item.pathName}/${item.date}`)}
              status={item.status}
            />
      }}
    />
  );
};

// --- Responsive Game Section Component ---
const ResponsiveGameSection = ({ games, showLocked, onLockedClick }) => {
  return (
    <>
      {/* Desktop layout (1024px and above) */}
      <div className={styles.desktopOnly}>
        <DesktopGameSection 
          games={games} 
          showLocked={showLocked} 
          onLockedClick={onLockedClick} 
        />
      </div>
      
      {/* Mobile/tablet layout (below 1024px) */}
      <div className={styles.mobileOnly}>
        <FullCarousel 
          items={games} 
          showLocked={showLocked} 
          onLockedClick={onLockedClick} 
        />
      </div>
    </>
  );
};

// Benefits Section Component
const BenefitsSection = () => {
  const [playersCount, setPlayersCount] = useState(null);

  useEffect(() => {
    (async () => {
      const count = await getPlayersCount();
      if (typeof count === 'number' && !Number.isNaN(count)) {
        setPlayersCount(count);
      }
    })();
  }, []);

  const countPhrase = playersCount
    ? `${new Intl.NumberFormat('it-IT').format(playersCount)} apprendenti`
    : 'migliaia di apprendenti';

  return (
    <section className={styles.benefitsSection}>
      <div className={styles.benefitsContent}>
        <h2 className={styles.benefitsTitle}>Perché già {countPhrase} hanno scelto i nostri Giochini?</h2>
        <p className={styles.benefitsSubtitle}>
          I giochi di parole non sono solo divertimento: sono uno dei modi più efficaci per acquisire e consolidare una lingua straniera.
        </p>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitItem}>
            <FaIcon className="fas fa-brain"  />
            <h3>Apprendimento attivo</h3>
            <p>Il cervello ricorda meglio ciò che scopre attivamente rispetto a ciò che legge passivamente.</p>
          </div>
          <div className={styles.benefitItem}>
            <FaIcon className="fas fa-repeat"  />
            <h3>Ripetizione naturale</h3>
            <p>I giochi creano cicli di ripetizione spaziata che rafforzano la memoria a lungo termine.</p>
          </div>
          <div className={styles.benefitItem}>
            <FaIcon className="fas fa-heart"  />
            <h3>Motivazione intrinseca</h3>
            <p>La sfida e il divertimento ti fanno tornare ogni giorno, costruendo un'abitudine duratura.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Account Promo Section Component
const AccountPromoSection = () => {
  const { isSignedIn } = useUser();
  
  // Don't show if user is already signed in
  if (isSignedIn) return null;
  
  return (
    <section className={styles.accountPromoSection}>
      <div className={styles.accountPromoContent}>
        <div className={styles.accountPromoIcon}>
          <FaIcon className="fas fa-user-plus"  />
        </div>
        <h2 className={styles.accountPromoTitle}>Gioca senza limiti, gratis!</h2>
        <p className={styles.accountPromoSubtitle}>
          Crea un account gratuito per giocare senza limiti, 
          guadagnare gettoncini e sincronizzare i tuoi progressi su tutti i dispositivi.
        </p>
        <div className={styles.accountPromoFeatures}>
          <div className={styles.accountPromoFeature}>
            <FaIcon className="fas fa-infinity"  />
            <span>Giochi illimitati</span>
          </div>
          <div className={styles.accountPromoFeature}>
            <FaIcon className="fas fa-coins"  />
            <span>Gettoncini bonus</span>
          </div>
          <div className={styles.accountPromoFeature}>
            <FaIcon className="fas fa-sync"  />
            <span>Sincronizzazione</span>
          </div>
        </div>
        <Link to="/registrati" className={styles.accountPromoButton}>
          <FaIcon className="fas fa-arrow-right"  />
          Scopri tutti i vantaggi
        </Link>
        <p className={styles.accountPromoNote}>
          Nessun costo, niente spam. Solo più giochi!
        </p>
      </div>
    </section>
  );
};

// Lella AI Promo Section Component
const LellaPromoSection = () => {
  return (
    <section className={styles.lellaPromoSection}>
      <div className={styles.lellaPromoContent}>
        <div className={styles.lellaPromoImageCol}>
          <img src="/lella.png" alt="Lella" className={styles.lellaPromoAvatar} />
        </div>
        <div className={styles.lellaPromoTextCol}>
          <span className={styles.lellaPromoBadge}>
            <FaIcon className="fa-solid fa-sparkles"  />
            Assistente AI
          </span>
          <h2 className={styles.lellaPromoTitle}>Conosci Lella?</h2>
          <p className={styles.lellaPromoDescription}>
            Dopo ogni partita, tocca una parola e chiedi a <strong>Lella</strong> di 
            spiegarti il significato, gli usi e le curiosità linguistiche — tutto 
            direttamente dentro il gioco, senza uscire dalla piattaforma.
          </p>
          <Link to="/assistente-ai" className={styles.lellaPromoLink}>
            Scopri come funziona <FaIcon className="fas fa-arrow-right"  />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Gettoncini Promo Section Component
const GettonciniPromoSection = () => {
  return (
    <section className={styles.gettonciniPromoSection}>
      <div className={styles.promoCard}>
        <div className={styles.promoIconWrapper}>
          <GettonciniIcon size={48} />
        </div>
        <div className={styles.promoContent}>
          <h3 className={styles.promoTitle}>Cosa sono i Gettoncini?</h3>
          <p className={styles.promoDescription}>
            I gettoncini sono la valuta dei Giochini. Guadagnali giocando e usali per sbloccare 
            funzioni premium. Registra un account gratuito e ricevi subito i tuoi primi 1.000 gettoncini!
          </p>
          <Link to="/gettoncini" className={styles.promoLink}>
            Scopri come funzionano <FaIcon className="fas fa-arrow-right"  />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Collaborazioni Promo Section Component
const CollaborazioniPromoSection = () => {
  return (
    <section className={styles.collaborazioniPromoSection}>
      <div className={styles.promoCard}>
        <div className={styles.promoIconWrapper}>
          <span className={styles.promoEmoji}>🤝</span>
        </div>
        <div className={styles.promoContent}>
          <h3 className={styles.promoTitle}>Insegni italiano?</h3>
          <p className={styles.promoDescription}>
            Sei un creator, un insegnante o gestisci una community di studenti di italiano? 
            Collaboriamo insieme per portare il divertimento dell'apprendimento ai tuoi studenti.
          </p>
          <Link to="/collaborazioni" className={styles.promoLink}>
            Scopri le opportunità <FaIcon className="fas fa-arrow-right"  />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section Component
const NewsletterSection = () => {
  const { isSignedIn, user } = useUser();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { updateBalance, transactionTypes } = useGettoncini();
  const { queueAlert } = useAlertQueueActions();
  const { getToken } = useAuth();
  const clerkEmail = user?.primaryEmailAddress?.emailAddress || '';

  useEffect(() => {
    // Check if already subscribed
    setIsSubscribed(hasNewsletterSignupCompleted());
    // Pre-fill email
    const savedEmail = localStorage.getItem('email') || clerkEmail || '';
    setEmail(savedEmail);
  }, [clerkEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const userId = localStorage.getItem('user-id');
      if (!userId) {
        throw new Error('No user ID found');
      }

      const token = isSignedIn
        ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
        : null;

      const result = await submitNewsletterSignup(
        email.trim(),
        token,
        'homepage_newsletter_block'
      );

      if (result.success) {
        if (email.trim() !== localStorage.getItem('email')) {
          localStorage.setItem('email', email.trim());
        }
        setCategoryConsent({ marketing: true, source: 'settings' });
        localStorage.setItem('newsletter-signup-completed', Date.now().toString());
        syncUserProfileDebounced();

        const alertPayload = resolveNewsletterSignupAlert({
          result,
          isSignedIn,
          transactionTypes,
        });
        if (alertPayload && queueAlert) {
          const balance = alertPayload?.payload?.gettoncini?.balance;
          if (typeof balance === 'number') {
            updateBalance(balance);
          }
          queueResourceAlertsFromPayload({
            queueAlert,
            payload: alertPayload.payload,
            isAnonymous: alertPayload.isAnonymous,
            firstAlertDelayMs: 1000,
            signedInDurationMs: 8000,
            anonymousDurationMs: 8000,
          });
        }

        trackNewsletterSignupAttempt(true);
        setSubmitStatus('success');
        setIsSubscribed(true);
      } else {
        trackNewsletterSignupAttempt(false);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      trackNewsletterSignupAttempt(false);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const subscribedEmail = localStorage.getItem('email') || clerkEmail || email;

  return (
    <section className={styles.newsletterSection}>
      <div className={styles.newsletterBackground}>
        <div className={`${styles.newsletterShape} ${styles.nlShape1}`} />
        <div className={`${styles.newsletterShape} ${styles.nlShape2}`} />
        <div className={`${styles.newsletterShape} ${styles.nlShape3}`} />
      </div>
      <div className={styles.newsletterContent}>
        <div className={styles.newsletterIcon}>
          <FaIcon className="fas fa-envelope-open-text"  />
        </div>
        <h2 className={styles.newsletterTitle}>Non perderti niente</h2>
        <p className={styles.newsletterSubtitle}>
          Iscriviti alla nostra newsletter per accedere in anteprima a nuovi giochi e ricevere consigli e contenuti esclusivi per imparare l'italiano direttamente nella tua casella di posta.
        </p>

        {isSubscribed || submitStatus === 'success' ? (
          <div className={styles.subscribedState}>
            <div className={styles.subscribedBadge}>
              <FaIcon className="fas fa-check-circle"  />
              <div className={styles.subscribedInfo}>
                <span className={styles.subscribedLabel}>Sei iscritto!</span>
                {subscribedEmail && (
                  <span className={styles.subscribedEmail}>{subscribedEmail}</span>
                )}
              </div>
            </div>
            <p className={styles.subscribedMessage}>
              Grazie per esserti iscritto! Riceverai presto le nostre novità.
            </p>
          </div>
        ) : (
          <form className={styles.newsletterForm} onSubmit={handleSubmit}>
            <div className={styles.newsletterInputGroup}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSubmitStatus(null);
                }}
                placeholder="La tua email..."
                className={styles.newsletterInput}
                disabled={isSubmitting}
                required
              />
              <button
                type="submit"
                className={`${styles.newsletterButton} ${isSubmitting ? styles.submitting : ''}`}
                disabled={isSubmitting || !email.trim()}
              >
                {isSubmitting ? (
                  <FaIcon className="fas fa-spinner fa-spin"  />
                ) : (
                  'Iscriviti'
                )}
              </button>
            </div>
            {submitStatus === 'error' && (
              <p className={styles.newsletterError}>
                Si è verificato un errore. Riprova più tardi.
              </p>
            )}
            <p className={styles.newsletterPrivacy}>
              Niente spam, solo contenuti utili. Puoi disiscriverti in qualsiasi momento.
            </p>
          </form>
        )}

        <Link to="/newsletter" className={styles.newsletterLearnMore}>
          Scopri di più sulla newsletter <FaIcon className="fas fa-arrow-right"  />
        </Link>
      </div>
    </section>
  );
};

// Streak Banner Component
const StreakBanner = () => {
  const { streakData, isLoading } = useStreak();
  const navigate = useNavigate();
  
  if (isLoading) {
    return null; // Don't show anything while loading
  }

  const currentStreak = streakData?.currentStreak || 0;
  const weekData = buildCurrentWeekDataFromDates(streakData?.completedDatesCurrentWeek) || getCurrentWeekCompletion();

  const handleClick = () => {
    navigate('/serie');
  };

  return (
    <div className={styles.streakBanner} onClick={handleClick}>
      <div className={styles.streakBannerContent}>
        <span className={styles.streakBannerNumber}>{currentStreak}</span>
        <span className={styles.streakBannerFire}>🔥</span>
        <div className={styles.streakBannerDivider} />
        <StreakIcons 
          weekData={weekData}
          showLabels={true}
          theme="dark"
          className={styles.streakBannerIcons}
        />
      </div>
    </div>
  );
};

// Daily Circuit Grid Component
export const DailyCircuitSection = ({ games, currentDate, sectionId = null, className = '' }) => {
  const { getGameWinReward, getGameWinMaxReward, getGameWinRewardRange } = useStellineRewards();
  // Filter to only today's games - games is an array of { gameId, games: [...] }
  const todayGames = games
    .map(game => {
      const todayGameData = game.games.find(g => g.date === currentDate);
      if (!todayGameData) return null;
      const gameConfig = getGameConfig(game.gameId);
      return {
        gameId: game.gameId,
        gameConfig: gameConfig,
        gameData: todayGameData,
        gameEntries: game.games,
      };
    })
    .filter(Boolean);

  if (todayGames.length === 0) return null;

  return (
    <section
      id={sectionId || undefined}
      className={`${styles.dailyCircuit} ${className}`.trim()}
    >
      <div className={styles.dailyCircuitGrid}>
        {todayGames.map((game, idx) => {
          if (!game || !game.gameConfig) return null;

          const GameIcon = game.gameConfig.icon;
          const gameData = game.gameData;
          const buttonText = getActionButtonText(gameData.status);
          const isCompleted = gameData.status === GAME_STATUS.FINISHED;
          const rewardAmount = game.gameId === 'ortografia' ? null : getGameWinReward(game.gameId);
          const rewardRange = game.gameId === 'ortografia' ? getGameWinRewardRange(game.gameId) : null;
          const maxRewardAmount = game.gameId === 'ortografia' ? getGameWinMaxReward(game.gameId) : null;
          const rewardChip = !isCompleted && (
            <StellineRewardChip
              amount={rewardAmount != null ? rewardAmount : maxRewardAmount}
              minAmount={rewardRange?.min}
              maxAmount={rewardRange?.max ?? maxRewardAmount}
              isMax={rewardAmount == null && maxRewardAmount != null}
              compact
              className={styles.dailyCircuitCardRewardChip}
            />
          );
          const cardThemeStyle = buildDailyCircuitThemeStyle(gameData.backgroundColor);
          const previousGames = game.gameEntries
            .filter((entry) => entry.date < currentDate)
            .sort((left, right) => right.date.localeCompare(left.date))
            .slice(0, 6);

          return (
            <article key={`${game.gameId}-${idx}`} className={styles.dailyCircuitCardShell}>
              <div className={styles.dailyCircuitDesktopCard}>
                <Link
                  to={`/${gameData.pathName}/${gameData.date}`}
                  className={styles.dailyCircuitCardLink}
                  aria-label={`${buttonText} ${gameData.title}`}
                >
                  <div className={styles.dailyCircuitCard} style={cardThemeStyle}>
                    <div className={styles.dailyCircuitCardContent}>
                      <div className={styles.dailyCircuitCardHeader}>
                        <div className={styles.dailyCircuitCardText}>
                          <div className={styles.dailyCircuitCardTitle}>{gameData.title}</div>
                          {!isCompleted && gameData.message && (
                            <div className={styles.dailyCircuitCardMessage}>{gameData.message}</div>
                          )}
                        </div>
                        <div className={styles.dailyCircuitCardMeta}>
                          <GameIcon
                            size={44}
                            status={gameData.status}
                            backgroundColor={gameData.backgroundColor}
                          />
                        </div>
                      </div>
                      <div className={styles.dailyCircuitCardFooter}>
                        <div className={styles.dailyCircuitCardDate}>{formatItalianDate(gameData.date)}</div>
                        {rewardChip}
                      </div>
                    </div>
                    {isCompleted && (
                      <div className={styles.dailyCircuitCompletedOverlay}>
                        <div
                          className={styles.dailyCircuitCompletedCheckmark}
                          style={{ backgroundColor: gameData.backgroundColor }}
                        >
                          <FaIcon className="fas fa-check" />
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
              <div className={styles.dailyCircuitMobileCarousel}>
                <div className={styles.dailyCircuitMobileTrack}>
                  <div className={styles.dailyCircuitMobileTodaySlide}>
                    <Link
                      to={`/${gameData.pathName}/${gameData.date}`}
                      className={styles.dailyCircuitCardLink}
                      aria-label={`${buttonText} ${gameData.title}`}
                    >
                      <div className={styles.dailyCircuitCard} style={cardThemeStyle}>
                        <div className={styles.dailyCircuitCardContent}>
                          <div className={styles.dailyCircuitCardHeader}>
                            <div className={styles.dailyCircuitCardText}>
                              <div className={styles.dailyCircuitCardTitle}>{gameData.title}</div>
                              {!isCompleted && gameData.message && (
                                <div className={styles.dailyCircuitCardMessage}>{gameData.message}</div>
                              )}
                            </div>
                            <div className={styles.dailyCircuitCardMeta}>
                              <GameIcon
                                size={40}
                                status={gameData.status}
                                backgroundColor={gameData.backgroundColor}
                              />
                            </div>
                          </div>
                          <div className={styles.dailyCircuitCardFooter}>
                            <div className={styles.dailyCircuitCardDate}>{formatItalianDate(gameData.date)}</div>
                            {rewardChip}
                          </div>
                        </div>
                        {isCompleted && (
                          <div className={styles.dailyCircuitCompletedOverlay}>
                            <div
                              className={styles.dailyCircuitCompletedCheckmark}
                              style={{ backgroundColor: gameData.backgroundColor }}
                            >
                              <FaIcon className="fas fa-check" />
                            </div>
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>
                  {previousGames.map((previousGame) => {
                    const isPreviousCompleted = previousGame.status === GAME_STATUS.FINISHED;
                    const pastBackgroundColor = isPreviousCompleted
                      ? darkenColor(previousGame.backgroundColor, 18)
                      : previousGame.backgroundColor;
                    const pastThemeStyle = buildDailyCircuitThemeStyle(pastBackgroundColor);

                    return (
                      <div key={`${game.gameId}-${previousGame.date}`} className={styles.dailyCircuitMobilePastSlide}>
                        <Link
                          to={`/${previousGame.pathName}/${previousGame.date}`}
                          className={styles.dailyCircuitCardLink}
                          aria-label={`${previousGame.title} ${getItalianDayOfWeek(previousGame.date)}`}
                        >
                          <div className={`${styles.dailyCircuitPastCard} ${isPreviousCompleted ? styles.dailyCircuitPastCardCompleted : ''}`} style={pastThemeStyle}>
                            <div className={styles.dailyCircuitPastCardCenter}>
                              {!isPreviousCompleted ? (
                                <GameIcon
                                  size={63}
                                  status={previousGame.status}
                                  backgroundColor={previousGame.backgroundColor}
                                />
                              ) : (
                                <div
                                  className={styles.dailyCircuitPastCardCheckmark}
                                  style={{ backgroundColor: previousGame.backgroundColor }}
                                >
                                  <FaIcon className="fas fa-check" />
                                </div>
                              )}
                            </div>
                            <div className={styles.dailyCircuitPastCardDay}>
                              {getItalianDayOfWeek(previousGame.date)}
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                  <div className={styles.dailyCircuitMobileArchiveSlide}>
                    <Link
                      to={`/${gameData.pathName}/archivio`}
                      className={styles.dailyCircuitMobileArchiveLink}
                      aria-label={`Vai all'archivio di ${gameData.title}`}
                    >
                      <div className={styles.dailyCircuitMobileArchiveCircle}>
                        <span>Cerchi più giochi?</span>
                        <FaIcon className="fas fa-chevron-right" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
              <Link
                to={`/${gameData.pathName}/archivio`}
                className={`${styles.heroArchiveButton} ${styles.dailyCircuitArchiveButton}`}
                aria-label={`Esplora l'archivio di ${gameData.title}`}
                style={cardThemeStyle}
              >
                <span>Archivio</span>
                <FaIcon className="fas fa-calendar-alt" />
              </Link>
            </article>
          );
        })}
      </div>
    </section>
  );
};

const HomePage = ({ 
  analyticsConsent, 
  setAnalyticsConsent, 
  onNavigationClick,
  showWelcomeMessage,
  setShowWelcomeMessage
}) => {
  const { isSignedIn, user } = useUser();
  const firstName = (isSignedIn && user?.firstName) ? user.firstName : null;
  const navigate = useNavigate();
  const location = useLocation();
  const { partner_slug: partnerSlug } = useParams();
  const snackAlertRef = useRef();
  const { openModal, closeModal, isOpen: isModalOpen } = useGlobalModal();
  
  // Track if partner welcome modal is currently showing (to handle close by any means)
  const partnerModalOpenRef = useRef(false);

  // Get current date and generate dates for the past week through tomorrow
  const currentDate = getCurrentDate();
  const today = useMemo(() => new Date(), []);
  const dates = useMemo(() => (
    Array.from({ length: 8 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - 6 + i);
      return toDateFormat(date);
    })
  ), [today]);

  // Get today's date in local timezone for comparison
  const todayStr = useMemo(() => toDateFormat(today), [today]);

  // Get all games from configuration, sorted by order, and exclude hidden ones
  const allGames = useMemo(() => getAllGames().filter(cfg => !cfg.hidden), []);

  // Generate all game data (needed for useGameStatuses hook)
  const allGameData = useMemo(() => (
    allGames.map(gameConfig => ({
      gameId: gameConfig.id,
      games: dates.map(date => ({
        date,
        title: gameConfig.displayName,
        gameId: gameConfig.id,
        pathName: gameConfig.pathName,
        backgroundColor: gameConfig.backgroundColor,
        isNew: !!gameConfig.is_new,
      }))
    }))
  ), [allGames, dates]);

  // Fetch server statuses (for all users - anonymous users will get null/not_started)
  const { getGameStatus: getServerGameStatus, isLoading: isLoadingStatuses } = useGameStatuses(allGameData, true, {
    scopeKey: 'home_feed',
    priority: 'high',
    subscribeOnly: false,
  });

  const greeting = useMemo(() => getGreeting(firstName), [firstName]);

  // Trigger homepage-enter logic only when actually on '/'
  useEffect(() => {
    if (location.pathname === '/' && onNavigationClick) {
      onNavigationClick();
    }
  }, [onNavigationClick, location.pathname]);

  // Show welcome message modal if needed
  useEffect(() => {
    if (showWelcomeMessage) {
      openModal({
        title: "Benvenuti! 👋",
        content: <WelcomeMessage onClose={closeModal} />
      });
      setShowWelcomeMessage(false);
    }
  }, [showWelcomeMessage, setShowWelcomeMessage, openModal, closeModal]);

  // Partner welcome modal via /benvenuti/:partner_slug route
  useEffect(() => {
    if (!partnerSlug) return;
    let cancelled = false;

    const seenKey = `partner_welcome_shown:${partnerSlug}`;
    try {
      if (localStorage.getItem(seenKey) === 'true') {
        navigate('/', { replace: true });
        return;
      }
    } catch (_) {}

    const loadPartnerCampaign = async () => {
      const campaignResponse = await getPartnerCampaign(partnerSlug);
      if (cancelled) return;

      const campaign = campaignResponse?.success ? campaignResponse.campaign : null;
      if (!campaign || campaign.show_benvenuti_message === false) {
        navigate('/', { replace: true });
        return;
      }

      // Mark that partner modal is open (dismiss logic runs in separate useEffect)
      partnerModalOpenRef.current = true;

      openModal({
        title: campaign.title || "Benvenuto",
        content: (
          <PartnerLandingContent
            title={campaign.title}
            bodyHtml={campaign.body_html}
            imageSrc={resolvePartnerImageUrl(campaign.image_url)}
            dismissLabel={campaign.dismiss_label}
            onDismiss={closeModal}
          />
        )
      });
    };

    loadPartnerCampaign();

    return () => {
      cancelled = true;
    };
  }, [partnerSlug, navigate, openModal, closeModal]);

  // Handle partner modal close (by any means: button, X, or overlay click)
  useEffect(() => {
    if (!isModalOpen && partnerModalOpenRef.current && partnerSlug) {
      partnerModalOpenRef.current = false;
      const seenKey = `partner_welcome_shown:${partnerSlug}`;
      try { localStorage.setItem(seenKey, 'true'); } catch (_) {}
      try {
        if (analyticsConsent) {
          logEvent('partner_welcome_shown', { partner: partnerSlug });
        }
      } catch (_) {}
      navigate('/', { replace: true });
    }
  }, [isModalOpen, partnerSlug, analyticsConsent, navigate]);

  // Deep-link: open user settings modal via query param
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const modal = params.get('modal');
      if (modal === 'settings' || modal === 'userSettings') {
        // Clean up URL immediately
        params.delete('modal');
        const newSearch = params.toString();
        navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
        
        openModal({
          title: "Impostazioni utente",
          content: <UserSettings onConsentChange={setAnalyticsConsent} />
        });
      }
    } catch (_) {}
  }, [location.search, setAnalyticsConsent, openModal, navigate, location.pathname]);

  // Generate game data for each game using the configuration
  const generateGameData = (gameConfig) => {
    return dates.map(date => {
      const isToday = date === todayStr;
      
      // Use the bundled default until a server status is available, including during refreshes.
      const serverStatus = getServerGameStatus(gameConfig.id, date);
      const status = serverStatus ?? GAME_STATUS.NOT_STARTED;
      
      let message;
      if (isToday) {
        message = getGameMessage(gameConfig.id, status);
      }

      return {
        title: gameConfig.displayName,
        gameId: gameConfig.id,
        pathName: gameConfig.pathName,
        date: date,
        icon: <gameConfig.icon size={48} />,
        message: message,
        backgroundColor: gameConfig.backgroundColor,
        status: status,
        isNew: !!gameConfig.is_new,
        isLoading: isLoadingStatuses,
      };
    });
  };

  // Generate final game data with statuses
  const finalGameData = allGames.map(gameConfig => ({
    gameId: gameConfig.id,
    games: generateGameData(gameConfig)
  }));

  return (
    <PageShell>
      <div data-google-interstitial="false">
        <SnackAlert ref={snackAlertRef} />
        <div className={styles.container}>
          {/* Top Intro */}
          <div className={styles.heroSection}>
            <div className={styles.heroMain}>
              <div className={styles.heroContent}>
                <p className={styles.heroGreeting}>{greeting}</p>
                <DailyCircuitSection
                  sectionId="homepage-daily-circuit"
                  games={finalGameData}
                  currentDate={currentDate}
                  className={styles.heroDailyCircuit}
                />
              </div>
            </div>
          </div>

          {/* Streak Banner */}
          <StreakBanner />

          {/* Benefits Section */}
          <BenefitsSection />

          {/* Account Promo Section */}
          <AccountPromoSection />

          {/* Testimonials Section */}
          <Testimonials />

          {/* Game Catalog Section */}
          <GameCatalogSection />

          {/* Lella AI Promo Section */}
          <LellaPromoSection />

          {/* Gettoncini Promo Section */}
          <GettonciniPromoSection />

          {/* Newsletter Section */}
          <NewsletterSection />

          {/* Collaborazioni Promo Section */}
          <CollaborazioniPromoSection />
        </div>
      </div>
    </PageShell>
  );
};

export default HomePage;
